import type { ItemFieldRow } from './item-field-reader';
import { readCreaturePayloadStatuses } from './creature-status-reader';
import { readItemField } from './item-field-reader';
import { readInventoryField, readLayerListEntryUuid } from './inventory-raw-readers';
import { readStatusRowStack } from './status-stack-reader';
import { isCombatStartTimedPassive } from './combat-start-timed-passive.js';
import { STATUS_RESIDUAL_LAYERS, statusLayerPayloadRetired } from './status-residue-layers';
import { janusContestSuccessImmunityMarker } from '../port/k3-rules/contest_success_immunity';

type Row = Record<string, unknown>;
const record = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const upper = (v: unknown): string => String(v ?? '').toUpperCase();
export const STATUS_ROOT_RESIDUE_LAYERS = ['extra_attack', 'no_healing_rolls', 'skill_rolls_mod', 'skill_is_rolled_at_min',
    'skill_is_rolled_at_max', 'truesight', 'magical_light', 'concentration_roll_is_rolled_at_min'] as const;
export const FOLD_ROOT_FLAGS = ['ignore_spell_component_verbal', 'ignore_spell_component_somatic', 'ignore_spell_component_material',
    'ranged_weapon_attacks_ignore_cover', 'ranged_spell_attacks_ignore_cover', 'ranged_attacks_ignore_cover', 'ranged_attacks_ignore_partial_cover',
    'adv_vs_own_marked_target', 'crit_to_hit', 'ignore_ranged_weapon_attack_roll_dis_within_an_enemy_reach',
    'ignore_ranged_spell_attack_roll_dis_within_an_enemy_reach', 'ignore_long_range_attack_roll_dis', 'attacks_ignore_damage_mitigation',
    'opportunity_attack_roll_adv', 'ranged_attacks_do_not_reveal_position', 'not_detected_by_truesight', 'not_detected_by_special_senses',
    'ignore_all_attack_roll_dis', 'overrun_roll_adv', 'attacks_against_cannot_gain_adv', 'spells_ignore_damage_mitigation',
    'damage_ignores_all_damage_mitigation', 'concentration_roll_is_rolled_at_max'] as const;
/** Array-valued layer writes stay separate from numeric root_scalars. null deletes the cold writer's slot. */
export type StatusRootFields = Record<string, unknown[] | null>;
export function applyStatusRootFields(row: Row, fields: StatusRootFields | undefined): void {
    let layers: Row | undefined;
    for (const [key, value] of Object.entries(fields ?? {})) {
        // Finalized creature rows pack these slots into item_layer_types; old/PC
        // rows can retain them at the root. Write the destination the cold writer used.
        const canonical = record(row.item_layer_types) && Object.prototype.hasOwnProperty.call(row.item_layer_types, key);
        const destination = canonical ? (layers ??= { ...row.item_layer_types as Row }) : row;
        if (value === null) delete destination[key]; else destination[key] = structuredClone(value);
    }
    if (layers) row.item_layer_types = layers;
}

interface RootReaders {
    definition: (id: string) => Row | null;
    entries: (row: ItemFieldRow, key: string) => unknown[];
    value: (row: ItemFieldRow | null, key: string) => unknown;
    active: (definition: Row, host: Row) => boolean;
    buckets: (host: Row) => Row;
    gearLayers: readonly string[];
    quantityIds: readonly string[];
    gearCounted: (host: Row, source: string, id: string, layer: string) => boolean;
}
const tree = (rows: unknown): Row[] => (Array.isArray(rows) ? rows : record(rows) ? Object.values(rows) : []).filter(record)
    .flatMap(row => [row, ...tree(row.inventory_in_container)]);
const senseLayers = new Set(['nightsight', 'fiendsight', 'blindsight', 'ghostsight', 'spellsight', 'truesight', 'tremorsense', 'telepathy', 'windsense']);
const scaledResidue = new Set(['skill_rolls_mod', 'ac_mod_magical', 'ac_mod_non_magical', 'ac_mod_other', 'ac_mod_environmental']);

/** The stored canonical or legacy layer is already a fragment list. layer_entries could dedupe UUIDs or prefer a
 * computed modifier, losing ordered duplicates. Sparse omission is an empty fragment list. */
function rootFragments(row: Row, key: string): unknown[] {
    // Match the native defense writer's stored-value projection: one extra wrapper preserves the entire root list.
    const stored = record(row.item_layer_types) && Object.prototype.hasOwnProperty.call(row.item_layer_types, key)
        ? row.item_layer_types[key] : row[key];
    const value = readItemField({ [key]: [stored ?? null] }, key);
    return value == null || value === '' ? [] : Array.isArray(value) ? structuredClone(value) : [structuredClone(value)];
}
type StatusReaders = Pick<RootReaders, 'definition' | 'entries' | 'value' | 'quantityIds'>;
function statusShares(host: Row, read: StatusReaders, layers: readonly string[]): Record<string, unknown[]> {
    const out: Record<string, unknown[]> = {};
    const add = (key: string, values: unknown[]) => { if (values.length) (out[key] ??= []).push(...values); };
    const statuses = Object.values(readCreaturePayloadStatuses(host, read.value, read.definition)).filter(record);
    for (const status of statuses) {
        const def = record(status.status_arr) ? status.status_arr : null;
        if (!def || readItemField(def, 'item_type') === 'creature'
            || statusLayerPayloadRetired(String(readItemField(def, 'item_id') ?? ''))
            || readItemField(status, 'status_casting_targets_arr') === janusContestSuccessImmunityMarker()) continue;
        const quantity = read.quantityIds.some(id => upper(id) === upper(readItemField(status, 'item_id')));
        for (const key of layers) add(key, read.entries(def, key).map(value => {
            if (!quantity || !scaledResidue.has(key)) return value;
            const text = typeof value === 'number' && Number.isInteger(value) ? String(value) : typeof value === 'string' ? value.trim() : '';
            if (!/^[+-]?\d+$/.test(text) || readStatusRowStack(status) <= 1) return value;
            const scaled = Number(text) * readStatusRowStack(status);
            return typeof value === 'number' ? scaled : text.startsWith('+') && scaled > 0 ? '+' + scaled : String(scaled);
        }));
    }
    return out;
}
function rootShares(host: Row, read: RootReaders): Record<string, unknown[]> {
    const out = statusShares(host, read, [...new Set([...STATUS_ROOT_RESIDUE_LAYERS, ...STATUS_RESIDUAL_LAYERS])]);
    const statuses = Object.values(readCreaturePayloadStatuses(host, read.value, read.definition)).filter(record);
    const add = (key: string, values: unknown[]) => { if (values.length) (out[key] ??= []).push(...values); };
    const inventory = tree(host.inventory), inventoryIds = new Set(inventory.map(row => upper(readInventoryField(row, 'inventory_id'))));
    const buckets = read.buckets(host);
    const flags = new Set<string>();
    const fold = (def: Row): void => {
        if (['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'].every(key => read.value(def, key) != null)) return;
        if (isCombatStartTimedPassive(def, read.value)
            && !statuses.some(status => upper(readItemField(status, 'item_id')) === upper(readItemField(def, 'item_id')))) return;
        if (!read.active(def, host)) return;
        for (const key of FOLD_ROOT_FLAGS) if (read.value(def, key) != null) flags.add(key);
    };
    for (const bucket of ['feat', 'passive_ability', 'status_effect_permanent']) {
        for (const entry of Array.isArray(buckets[bucket]) ? buckets[bucket] as unknown[] : []) {
            const id = upper(readLayerListEntryUuid(entry)), def = read.definition(id);
            if (!def || (record(entry) && entry.passive_conditions_active === false) || !read.active(def, host)) continue;
            if (bucket === 'feat' || bucket === 'passive_ability') fold(def);
            if (bucket === 'feat') for (const child of read.entries(def, 'passive_ability')) {
                const passive = read.definition(upper(readLayerListEntryUuid(child)));
                if (passive) fold(passive);
            }
            const source = record(entry) ? upper(readItemField(entry, 'source')) : '';
            if (!inventoryIds.has(source)) continue;
            for (const key of read.gearLayers) {
                // These integer slots are consumed into computed clusters; passive senses have their own source walk.
                if (['damage_mitigation', 'ac_mod_magical', 'ac_mod_non_magical'].includes(key)
                    || (bucket === 'passive_ability' && (senseLayers.has(key) || read.gearCounted(host, source, id, key)))) continue;
                add(key, read.entries(def, key));
            }
        }
    }
    for (const key of flags) out[key] = [1];
    return out;
}

/** Replace the compiler-owned shares, retaining authored/other-provider fragments and their multiplicity. No foreign-root refusal:
 * the browser owns the final row, including rows loaded cold with no historical C1 installer stamp. */
export function compileStatusRootFields(before: Row, after: Row, read: RootReaders): StatusRootFields {
    return replaceShares(before, rootShares(before, read), rootShares(after, read));
}

/** A status-only delta never walks equipment or passive grants, nor clones a member. */
export function compileStatusResidueFields(before: Row, after: Row, read: StatusReaders, layers: readonly string[]): StatusRootFields {
    if (!layers.length) return {};
    return replaceShares(before, statusShares(before, read, layers), statusShares(after, read, layers));
}
function replaceShares(before: Row, old: Record<string, unknown[]>, next: Record<string, unknown[]>): StatusRootFields {
    const fields: StatusRootFields = {};
    for (const key of new Set([...Object.keys(old), ...Object.keys(next)])) {
        if (JSON.stringify(old[key] ?? []) === JSON.stringify(next[key] ?? [])) continue;
        const own = rootFragments(before, key);
        for (const fragment of old[key] ?? []) {
            const at = own.findIndex(value => JSON.stringify(value) === JSON.stringify(fragment));
            if (at >= 0) own.splice(at, 1);
        }
        const values = [...own, ...(next[key] ?? [])];
        fields[key] = values.length ? values : null;
    }
    return fields;
}
